import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  GitFork, Plus, Trash2, Edit2, Play, Pause, Search, 
  Filter, MoreVertical, Clock, CheckCircle, XCircle,
  Mail, MessageSquare, MessageCircle, Activity, Zap,
  FileText, History, Copy, Eye, X, ChevronLeft, ChevronRight, Loader2, Variable,
  AlertTriangle
} from 'lucide-react';
import { workflowsV2API } from '../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ACTION_ICONS = {
  send_email: Mail, send_sms: MessageSquare, send_whatsapp: MessageCircle,
  create_task: CheckCircle, update_lead: Edit2, assign_lead: Zap, send_notification: Activity,
};
const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700 border-green-200',
  inactive: 'bg-gray-100 text-gray-600 border-gray-200',
  draft: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const TEMPLATE_CATEGORIES = [
  'Welcome Email', 'Follow-up', 'Payment Reminder', 'Booking Confirmation',
  'OTA Completion', 'Custom',
];
const TEMPLATE_VARIABLES = [
  { name: '{{lead.hotelName}}', desc: 'Hotel / Property Name' },
  { name: '{{lead.ownerName}}', desc: 'Owner Name' },
  { name: '{{lead.email}}', desc: 'Email Address (login username)' },
  { name: '{{lead.username}}', desc: 'Owner login ID (from Admin Panel)' },
  { name: '{{lead.password}}', desc: 'Temporary password (Account Creation only)' },
  { name: '{{lead.mobileNumber}}', desc: 'Phone Number' },
  { name: '{{lead.city}}', desc: 'City' },
  { name: '{{lead.state}}', desc: 'State' },
  { name: '{{lead.leadStage}}', desc: 'Lead Stage' },
  { name: '{{lead.salesOwner}}', desc: 'Sales Owner' },
  { name: '{{currentDate}}', desc: 'Current Date' },
];

// ==================== WORKFLOW CARD ====================
const WorkflowCard = ({ workflow, onDelete, onToggle, onEdit }) => {
  const [showMenu, setShowMenu] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer group"
      onClick={() => onEdit(workflow.id)} data-testid={`workflow-card-${workflow.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-800 truncate">{workflow.name}</h3>
          {workflow.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{workflow.description}</p>}
        </div>
        <div className="relative ml-2" onClick={e => e.stopPropagation()}>
          <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 rounded-lg hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"><MoreVertical size={16} className="text-gray-500" /></button>
          {showMenu && (<>
            <div className="fixed inset-0" onClick={() => setShowMenu(false)} />
            <div className="absolute right-0 top-8 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 w-40">
              <button onClick={() => { onToggle(workflow.id); setShowMenu(false); }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2">
                {workflow.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                {workflow.status === 'active' ? 'Deactivate' : 'Activate'}
              </button>
              <button onClick={() => { onDelete(workflow.id); setShowMenu(false); }} className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><Trash2 size={14} /> Delete</button>
            </div>
          </>)}
        </div>
      </div>
      <div className="mb-3"><span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full"><Zap size={12} />{workflow.trigger_event?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span></div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {(workflow.actions || []).slice(0, 3).map((a, i) => { const Icon = ACTION_ICONS[a.type] || Zap; return <div key={i} className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center" title={a.type}><Icon size={12} className="text-gray-600" /></div>; })}
        </div>
        <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${STATUS_COLORS[workflow.status] || STATUS_COLORS.draft}`}>{workflow.status?.charAt(0).toUpperCase() + workflow.status?.slice(1)}</span>
      </div>
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><Activity size={12} /> {workflow.run_count || 0} runs</span>
        <span className="flex items-center gap-1"><CheckCircle size={12} className="text-green-500" /> {workflow.success_count || 0}</span>
        <span className="flex items-center gap-1"><XCircle size={12} className="text-red-500" /> {workflow.fail_count || 0}</span>
      </div>
    </div>
  );
};

// ==================== TEMPLATES TAB ====================
const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modal, setModal] = useState({ open: false, template: null });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetch_templates = useCallback(async () => {
    setLoading(true);
    try {
      const params = typeFilter ? `?type=${typeFilter}` : '';
      const res = await fetch(`${API_BASE}/api/templates${params}`, { headers });
      if (res.ok) { const d = await res.json(); setTemplates(d.data || []); }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { fetch_templates(); }, [fetch_templates]);

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      const isEdit = !!formData.id;
      const url = isEdit ? `${API_BASE}/api/templates/${formData.id}` : `${API_BASE}/api/templates`;
      const method = isEdit ? 'PUT' : 'POST';
      const { id, ...payload } = formData;
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) {
        setModal({ open: false, template: null });
        fetch_templates();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to save template');
      }
    } catch { alert('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await fetch(`${API_BASE}/api/templates/${id}`, { method: 'DELETE', headers });
    fetch_templates();
  };

  const handleDuplicate = async (t) => {
    const dup = { name: `${t.name} (Copy)`, type: t.type, subject: t.subject, body: t.body, variables: t.variables || [], category: t.category, is_active: false };
    await fetch(`${API_BASE}/api/templates`, { method: 'POST', headers, body: JSON.stringify(dup) });
    fetch_templates();
  };

  const handleToggle = async (t) => {
    await fetch(`${API_BASE}/api/templates/${t.id}`, { method: 'PUT', headers, body: JSON.stringify({ is_active: !t.is_active }) });
    fetch_templates();
  };

  const filtered = templates.filter(t => !search || t.name?.toLowerCase().includes(search.toLowerCase()) || t.subject?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none" data-testid="template-search" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none" data-testid="template-type-filter">
          <option value="">All Types</option>
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="both">Both (Email + WhatsApp)</option>
        </select>
        <button onClick={() => setModal({ open: true, template: null })} className="flex items-center gap-1 px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700" data-testid="create-template-btn"><Plus size={16} /> New Template</button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="animate-spin text-teal-600" size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FileText size={32} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No templates found</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(t => (
            <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-800 text-sm truncate">{t.name}</h4>
                  {t.subject && <p className="text-xs text-gray-500 truncate mt-0.5">{t.subject}</p>}
                </div>
                <span className={`shrink-0 ml-2 px-2 py-0.5 text-[10px] font-semibold rounded-full ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{t.is_active ? 'Active' : 'Inactive'}</span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-700">{t.type}</span>
                {t.category && <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-purple-50 text-purple-700">{t.category}</span>}
              </div>
              <div className="flex items-center gap-1 pt-2 border-t border-gray-100">
                <button onClick={() => setModal({ open: true, template: t })} className="p-1.5 rounded hover:bg-gray-100" title="Edit"><Edit2 size={13} className="text-gray-500" /></button>
                <button onClick={() => setPreview(t)} className="p-1.5 rounded hover:bg-gray-100" title="Preview"><Eye size={13} className="text-gray-500" /></button>
                <button onClick={() => handleDuplicate(t)} className="p-1.5 rounded hover:bg-gray-100" title="Duplicate"><Copy size={13} className="text-gray-500" /></button>
                <button onClick={() => handleToggle(t)} className="p-1.5 rounded hover:bg-gray-100" title="Toggle">{t.is_active ? <Pause size={13} className="text-amber-500" /> : <Play size={13} className="text-green-500" />}</button>
                <div className="flex-1" />
                <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded hover:bg-red-50" title="Delete"><Trash2 size={13} className="text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Modal */}
      {modal.open && <TemplateModal template={modal.template} onClose={() => setModal({ open: false, template: null })} onSave={handleSave} saving={saving} />}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800">{preview.name}</h3>
              <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              {preview.subject && <div><span className="text-xs font-semibold text-gray-500">Subject:</span><p className="text-sm text-gray-800">{preview.subject}</p></div>}
              <div><span className="text-xs font-semibold text-gray-500">Body:</span><div className="mt-1 text-sm text-gray-700 bg-gray-50 rounded-lg p-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: preview.body }} /></div>
              {preview.variables?.length > 0 && <div><span className="text-xs font-semibold text-gray-500">Variables:</span><div className="flex flex-wrap gap-1 mt-1">{preview.variables.map(v => <span key={v} className="px-2 py-0.5 text-[10px] font-mono bg-teal-50 text-teal-700 rounded">{`{{${v}}}`}</span>)}</div></div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TEMPLATE MODAL ====================
const TemplateModal = ({ template, onClose, onSave, saving }) => {
  const [form, setForm] = useState({
    id: template?.id || '',
    name: template?.name || '',
    type: template?.type || 'email',
    subject: template?.subject || '',
    body: template?.body || '',
    variables: template?.variables || [],
    category: template?.category || '',
    is_active: template?.is_active ?? true,
  });
  const [showVars, setShowVars] = useState(false);

  const insertVar = (varName) => {
    setForm(p => ({ ...p, body: p.body + varName }));
    if (!form.variables.includes(varName.replace(/\{\{|\}\}/g, ''))) {
      setForm(p => ({ ...p, variables: [...p.variables, varName.replace(/\{\{|\}\}/g, '')] }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" data-testid="template-modal">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-800">{template ? 'Edit Template' : 'Create Template'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-teal-500 outline-none" data-testid="template-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Type</label>
              <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="w-full px-3 py-2 text-sm border rounded-lg outline-none" data-testid="template-type-select">
                <option value="email">Email</option><option value="whatsapp">WhatsApp</option><option value="both">Both (Email + WhatsApp)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full px-3 py-2 text-sm border rounded-lg outline-none">
                <option value="">Select category</option>
                {TEMPLATE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="rounded border-gray-300 text-teal-600" /> Active</label>
            </div>
          </div>
          {(form.type === 'email' || form.type === 'both') && (
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Subject</label>
              <input type="text" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Email subject line" className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-teal-500 outline-none" data-testid="template-subject-input" />
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-gray-600">Body *</label>
              <button onClick={() => setShowVars(!showVars)} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"><Variable size={12} /> Variables</button>
            </div>
            {showVars && (
              <div className="mb-2 p-2 bg-teal-50 rounded-lg flex flex-wrap gap-1">
                {TEMPLATE_VARIABLES.map(v => (
                  <button key={v.name} onClick={() => insertVar(v.name)} className="px-2 py-1 text-[10px] font-mono bg-white border border-teal-200 rounded hover:bg-teal-100 transition-colors" title={v.desc}>{v.name}</button>
                ))}
              </div>
            )}
            <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} rows={8} placeholder="Template body (HTML supported)" className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-teal-500 outline-none resize-y font-mono" data-testid="template-body-input" />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={() => onSave(form)} disabled={saving || !form.name || !form.body} className="flex items-center gap-1 px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50" data-testid="template-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} {template ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== LOGS TAB ====================
const LogActionRow = ({ action }) => {
  const [open, setOpen] = useState(false);
  const d = action?.details || {};
  const success = (action?.status || '').toLowerCase() === 'success';
  const waiting = (action?.status || '').toLowerCase() === 'waiting_credentials';
  const skipped = (action?.status || '').toLowerCase() === 'skipped' || waiting;
  return (
    <div className="border border-gray-200 rounded-lg" data-testid={`log-action-${action?.type || 'unknown'}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {success ? <CheckCircle size={14} className="text-emerald-600 shrink-0" />
            : waiting ? <Clock size={14} className="text-sky-600 shrink-0" />
            : skipped ? <Clock size={14} className="text-amber-600 shrink-0" />
            : <XCircle size={14} className="text-rose-600 shrink-0" />}
          <span className="font-mono text-xs text-gray-700">{action?.type || 'action'}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${success ? 'bg-emerald-100 text-emerald-700' : waiting ? 'bg-sky-100 text-sky-700' : skipped ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
            {waiting ? 'Waiting for Credentials' : action?.status}
          </span>
          {!success && !waiting && (d.error || action?.message) && (
            <span className="text-xs text-gray-600 truncate">— {d.error || action?.message}</span>
          )}
        </div>
        <ChevronRight size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-3 py-3 space-y-3 text-xs" data-testid={`log-action-details-${action?.type || 'unknown'}`}>
          {/* Status diagnostic chips */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 font-mono text-[11px] text-gray-500">
            <span>HTTP: <span className="text-gray-800">{d.meta_status_code ?? '—'}</span></span>
            <span>code: <span className="text-gray-800">{d.error_code ?? '—'}</span></span>
            <span>subcode: <span className="text-gray-800">{d.error_subcode ?? '—'}</span></span>
            <span>fbtrace: <span className="text-gray-800 truncate">{d.fbtrace_id ?? '—'}</span></span>
            <span>mode: <span className="text-gray-800">{d.mode ?? '—'}</span></span>
            <span>wamid: <span className="text-gray-800 truncate">{d.message_id ?? '—'}</span></span>
            <span>db_id: <span className="text-gray-800 truncate">{d.db_id ?? '—'}</span></span>
            <span>success: <span className="text-gray-800">{String(d.success ?? success)}</span></span>
          </div>
          {/* Error body */}
          {d.error && (
            <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              <p className="text-[11px] font-semibold text-rose-800 mb-0.5">Error</p>
              <p className="text-[12px] text-rose-900 break-words">{d.error}</p>
            </div>
          )}
          {/* Request payload */}
          {d.request_url && (
            <details className="bg-white border border-gray-200 rounded-md">
              <summary className="px-3 py-2 cursor-pointer text-[11px] font-semibold text-gray-700 hover:bg-gray-50">
                Request payload — {d.request_url}
              </summary>
              <pre className="px-3 py-2 text-[11px] bg-gray-50 overflow-auto max-h-60 border-t border-gray-200" data-testid="log-request-payload">
{JSON.stringify(d.request_payload, null, 2)}
              </pre>
            </details>
          )}
          {/* Meta response JSON */}
          {d.meta_response !== undefined && d.meta_response !== null && (
            <details open className="bg-white border border-gray-200 rounded-md">
              <summary className="px-3 py-2 cursor-pointer text-[11px] font-semibold text-gray-700 hover:bg-gray-50">
                Meta API response (HTTP {d.meta_status_code ?? '—'})
              </summary>
              <pre className="px-3 py-2 text-[11px] bg-gray-50 overflow-auto max-h-60 border-t border-gray-200" data-testid="log-meta-response">
{JSON.stringify(d.meta_response, null, 2)}
              </pre>
            </details>
          )}
          {/* Fallback: raw details */}
          {!d.meta_response && !d.request_url && !d.error && (
            <details className="bg-white border border-gray-200 rounded-md">
              <summary className="px-3 py-2 cursor-pointer text-[11px] font-semibold text-gray-700 hover:bg-gray-50">Raw action result</summary>
              <pre className="px-3 py-2 text-[11px] bg-gray-50 overflow-auto max-h-60 border-t border-gray-200">
{JSON.stringify(action, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

const LogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 25;
  const token = localStorage.getItem('token');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API_BASE}/api/workflow-logs?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setLogs(d.data || []); setTotal(d.total || 0); }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page, search, statusFilter, token]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  const totalPages = Math.ceil(total / limit);

  const statusBadge = (s) => {
    if (s === 'success' || s === 'completed') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700"><CheckCircle size={11} />{s === 'completed' ? 'Completed' : 'Success'}</span>;
    if (s === 'waiting_for_credentials') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700"><Clock size={11} />Waiting for Credentials</span>;
    if (s === 'partial_failure') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700"><AlertTriangle size={11} />Partial</span>;
    if (s === 'error' || s === 'failed') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700"><XCircle size={11} />Failed</span>;
    if (s === 'skipped') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700"><Clock size={11} />Skipped</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{s}</span>;
  };

  return (
    <div className="space-y-4" data-testid="logs-tab">
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search logs..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none">
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="partial_failure">Partial Failure</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
      </div>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-left text-sm">
          <thead><tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Workflow</th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Trigger</th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Started</th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Details</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            : logs.length === 0 ? <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">No logs found</td></tr>
            : logs.map(l => {
              const isOpen = expandedId === l.id;
              return (
                <React.Fragment key={l.id}>
                  <tr className="hover:bg-gray-50" data-testid={`workflow-log-row-${l.id}`}>
                    <td className="px-4 py-3 font-medium text-gray-800">{l.workflow_name}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">{l.trigger_event}</span></td>
                    <td className="px-4 py-3">{statusBadge(l.status)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{(l.actions_executed || []).map(a => a.type).join(', ') || '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{l.started_at ? new Date(l.started_at).toLocaleString() : '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setExpandedId(isOpen ? null : l.id)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border transition-colors ${isOpen ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                        data-testid={`workflow-log-view-details-${l.id}`}
                      >
                        <Eye size={11} /> {isOpen ? 'Hide' : 'View Details'}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-gray-50/60" data-testid={`workflow-log-details-${l.id}`}>
                      <td colSpan="6" className="px-4 py-4">
                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-mono text-gray-500">
                            <span>log_id: <span className="text-gray-800">{l.id}</span></span>
                            <span>workflow_id: <span className="text-gray-800 truncate">{l.workflow_id}</span></span>
                            <span>lead_id: <span className="text-gray-800 truncate">{l.lead_id || '—'}</span></span>
                            <span>completed_at: <span className="text-gray-800">{l.completed_at || '—'}</span></span>
                          </div>
                          {l.error_message && (
                            <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                              <p className="text-[11px] font-semibold text-rose-800 mb-0.5">Workflow-level error</p>
                              <p className="text-[12px] text-rose-900 break-words">{l.error_message}</p>
                            </div>
                          )}
                          <div className="space-y-2">
                            <p className="text-[11px] font-semibold text-gray-600 uppercase">Actions ({(l.actions_executed || []).length})</p>
                            {(l.actions_executed || []).length === 0 && (
                              <p className="text-xs text-gray-400">No actions executed.</p>
                            )}
                            {(l.actions_executed || []).map((a, i) => (
                              <LogActionRow key={i} action={a} />
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">Page {page} of {totalPages} ({total} total)</p>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 border rounded-lg disabled:opacity-30"><ChevronLeft size={14} /></button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-1.5 border rounded-lg disabled:opacity-30"><ChevronRight size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN WORKFLOWS PAGE ====================
const Workflows = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('workflows');
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { if (activeTab === 'workflows') loadWorkflows(); }, [statusFilter, activeTab]);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await workflowsV2API.getAll(params);
      setWorkflows(response.data.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => { if (window.confirm('Delete this workflow?')) { await workflowsV2API.delete(id); loadWorkflows(); } };
  const handleToggle = async (id) => { await workflowsV2API.toggle(id); loadWorkflows(); };
  const handleEdit = (id) => navigate(`/workflow/${id}`);

  const filteredWorkflows = workflows.filter(wf => wf.name?.toLowerCase().includes(searchQuery.toLowerCase()));
  const stats = { total: workflows.length, active: workflows.filter(w => w.status === 'active').length, inactive: workflows.filter(w => w.status === 'inactive').length, draft: workflows.filter(w => w.status === 'draft').length };

  const tabs = [
    { id: 'workflows', label: 'Workflows', icon: GitFork },
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'logs', label: 'Execution Logs', icon: History },
  ];

  return (
    <div className="space-y-6" data-testid="workflows-page">
      {/* Header with tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Workflow Automation</h2>
            <p className="text-sm text-gray-500 mt-0.5">Automate actions, manage templates, and view execution logs</p>
          </div>
          {activeTab === 'workflows' && (
            <button onClick={() => navigate('/workflow/new')} className="flex items-center px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm font-medium" data-testid="create-workflow-btn"><Plus size={20} className="mr-2" /> Create Workflow</button>
          )}
        </div>
        <div className="flex border-t border-gray-100 px-5">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'workflows' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total', value: stats.total, color: 'bg-blue-500' },
              { label: 'Active', value: stats.active, color: 'bg-green-500' },
              { label: 'Inactive', value: stats.inactive, color: 'bg-gray-400' },
              { label: 'Drafts', value: stats.draft, color: 'bg-yellow-500' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3"><div className={`w-2 h-8 rounded-full ${s.color}`} /><div><p className="text-2xl font-bold text-gray-900">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div></div>
              </div>
            ))}
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative"><Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search workflows..." className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" data-testid="workflow-search" /></div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 outline-none" data-testid="workflow-status-filter"><option value="all">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="draft">Draft</option></select>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="animate-spin text-teal-600" size={28} /></div>
          ) : filteredWorkflows.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{filteredWorkflows.map(w => <WorkflowCard key={w.id} workflow={w} onDelete={handleDelete} onToggle={handleToggle} onEdit={handleEdit} />)}</div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <GitFork size={32} className="text-gray-300 mx-auto mb-3" /><p className="text-gray-500">No workflows found</p>
            </div>
          )}
        </>
      )}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'logs' && <LogsTab />}
    </div>
  );
};

export default Workflows;
